import { z } from "zod";
import { optionalEnum, optionalUuid } from "./_helpers/optional";

const submissionTypes = [
  "field_note",
  "issue_report",
  "site_assessment",
  "progress_update",
  "completion",
  "system",
] as const;

const mediaSources = [
  "mobile",
  "web",
  "api",
  "csv_import",
  "companycam_sync",
  "integration_sync",
  "pipeline",
] as const;

const photoStages = ["before", "during", "after", "issue", "general"] as const;

const aiCaptionStatuses = ["pending", "processing", "done", "skipped"] as const;

const uploadStatuses = [
  "pending",
  "uploaded",
  "processing",
  "ready",
  "failed",
] as const;

// Mirror the DB CHECK from the adv_1111 migration
// (supabase/migrations/20260524051000_adv_1111_comm_attachment_media_item_fk.sql:29-46)
// + the warranty_claim addition in adv_1180 + the service_agreement addition
// in adv_1181.
// Was 4 values until ADV-1115 reconciled the Zod drift; ADV-1180 added
// 'warranty_claim' alongside the obligation type 'warranty'; ADV-1181 added
// 'service_agreement' (service_plan is a template, excluded).
// Also exported as `mediaSubjectEntityTypes` so sibling schemas
// (ADV-1132 FinalizeMessage) stay in lockstep without duplicating the tuple.
export const mediaSubjectEntityTypes = [
  "job",
  "opportunity",
  "proposal",
  "warranty",
  "warranty_claim",
  "service_agreement",
  "appointment",
  "invoice",
  "customer",
] as const;
const entityTypes = mediaSubjectEntityTypes;

// Subset of entityTypes that the media→entity *association* write path can
// resolve. `entityTypes` above is the full set valid for media_subjects.entity_type
// (the activity feed reads all of them), but POST /media/sessions/{id}/associate
// can only attach to entities whose table is in ENTITY_TABLE_MAP
// (packages/services/src/ops/media-service.ts). Keep this list in lockstep with
// that map's keys — otherwise the endpoint accepts an input it then 500s on
// ("Unknown entity type"). (ADV-1115 review remediation.)
const associableEntityTypes = [
  "job",
  "opportunity",
  "proposal",
  "warranty",
  "warranty_claim",
  "service_agreement",
] as const;

// ADV-1123 / ADR-0045: async digital-asset processing pipeline.
// processing_status is a 10-state machine; existing rows default to the
// terminal 'active' state at the DB layer (DEFAULT 'active', no backfill).
const processingStatuses = [
  "pending",
  "downloading",
  "downloaded",
  "validating",
  "validated",
  "finalizing",
  "active",
  "failed_download",
  "failed_validation",
  "failed_finalization",
] as const;

const lastErrorStages = ["download", "validate", "finalize"] as const;
const lastErrorCategories = [
  "permanent",
  "transient_fast",
  "transient_slow",
  "rate_limited",
  "network",
  "ssrf_blocked",
] as const;

export const MediaSubjectSchema = z.object({
  id: z.string().uuid().describe("ID"),
  submission_id: z.string().uuid().describe("Submission"),
  business_id: z.string().uuid().describe("Business"),
  entity_type: z.enum(entityTypes).describe("Entity Type"),
  entity_id: z.string().uuid().describe("Entity"),
});
export type MediaSubject = z.infer<typeof MediaSubjectSchema>;

export const MediaItemSchema = z.object({
  id: z.string().uuid().describe("ID"),
  submission_id: z.string().uuid().describe("Submission"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  storage_key: z.string().describe("Storage Key"),
  storage_bucket: z.string().describe("Storage Bucket"),
  public_url: z.string().nullable().describe("Public URL"),
  content_type: z.string().describe("Content Type"),
  file_size_bytes: z.number().nullable().describe("File Size (Bytes)"),
  width_px: z.number().int().nullable().describe("Width (px)"),
  height_px: z.number().int().nullable().describe("Height (px)"),
  photo_stage: z.enum(photoStages).nullable().describe("Photo Stage"),
  subject_tags: z.array(z.string()).describe("Subject Tags"),
  caption: z.string().nullable().describe("Caption"),
  ai_caption: z.string().nullable().describe("AI Caption"),
  ai_caption_status: z.enum(aiCaptionStatuses).describe("AI Caption Status"),
  display_order: z.number().int().describe("Display Order"),
  upload_status: z.enum(uploadStatuses).describe("Upload Status"),
  uploaded_at: z.string().nullable().describe("Uploaded At"),
  external_id: z.string().nullable().describe("External ID"),
  captured_at: z.string().nullable().describe("Captured At"),
  gps_latitude: z.number().nullable().describe("GPS Latitude"),
  gps_longitude: z.number().nullable().describe("GPS Longitude"),
  exif_data: z.record(z.unknown()).describe("EXIF Data"),
  source_url: z.string().nullable().describe("Source URL"),
  // ADV-1162: the import job that created this item (NULL for operator/app
  // uploads). Set by the comm-attachment async insert; read by the pipeline.
  import_job_id: z.string().uuid().nullable().describe("Import Job"),
  // ADV-1123 / ADR-0045: async digital-asset processing pipeline.
  processing_status: z.enum(processingStatuses).describe("Processing Status"),
  etag: z.string().nullable().describe("ETag"),
  last_modified: z.string().nullable().describe("Last Modified"),
  cache_control: z.string().nullable().describe("Cache Control"),
  content_sha256: z.string().nullable().describe("Content SHA-256"), // base64
  content_dedup_of: z.string().uuid().nullable().describe("Content Dedup Of"),
  staging_key: z.string().nullable().describe("Staging Key"),
  format: z.string().nullable().describe("Format"),
  aspect_ratio: z.number().nullable().describe("Aspect Ratio"),
  pixel_count: z.number().nullable().describe("Pixel Count"),
  has_alpha: z.boolean().nullable().describe("Has Alpha"),
  exif_orientation: z.number().int().nullable().describe("EXIF Orientation"),
  color_space: z.string().nullable().describe("Color Space"),
  density_dpi: z.number().int().nullable().describe("Density (DPI)"),
  camera_make: z.string().nullable().describe("Camera Make"),
  camera_model: z.string().nullable().describe("Camera Model"),
  retry_count: z.number().int().describe("Retry Count"),
  last_attempted_at: z.string().nullable().describe("Last Attempted At"),
  last_error_message: z.string().nullable().describe("Last Error Message"),
  last_error_at: z.string().nullable().describe("Last Error At"),
  last_error_stage: z
    .enum(lastErrorStages)
    .nullable()
    .describe("Last Error Stage"),
  last_error_category: z
    .enum(lastErrorCategories)
    .nullable()
    .describe("Last Error Category"),
  created_at: z.string().describe("Created At"),
  deleted_at: z.string().nullable().describe("Deleted At"),
});
export type MediaItem = z.infer<typeof MediaItemSchema>;

export const MediaSubmissionSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  submitted_by: z.string().uuid().nullable().describe("Submitted By"),
  submitted_at: z.string().describe("Submitted At"),
  message: z.string().nullable().describe("Message"),
  submission_type: z.enum(submissionTypes).describe("Submission Type"),
  location_id: z.string().uuid().nullable().describe("Location"),
  source: z.enum(mediaSources).describe("Source"),
  external_source: z.string().nullable().describe("External Source"),
  external_id: z.string().nullable().describe("External ID"),
  deleted_at: z.string().nullable().describe("Deleted At"),
  items: z.array(MediaItemSchema).optional().describe("Items"),
  subjects: z.array(MediaSubjectSchema).optional().describe("Subjects"),
  // ADV-1344: resolved submitter identity for the activity feed. Populated only
  // when read through ops.media_submissions_feed_v — the LATERAL resolves
  // submitted_by (a contact_roles.id) → contact_roles → contacts (name) +
  // team_member_attributes.headshot_url (avatar), business-scoped. Optional +
  // nullable so non-feed reads of the base table degrade to a monogram.
  actor_name: z.string().nullable().optional().describe("Submitted By Name"),
  actor_avatar_url: z
    .string()
    .nullable()
    .optional()
    .describe("Submitted By Avatar"),
});
export type MediaSubmission = z.infer<typeof MediaSubmissionSchema>;

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

export const MediaItemUploadSchema = z.object({
  content_type: z.string().describe("Content Type"),
  file_size_bytes: z
    .number()
    .int()
    .positive()
    .optional()
    .describe("File Size (Bytes)"),
  display_order: z.number().int().min(0).default(0).describe("Display Order"),
  caption: z.string().optional().describe("Caption"),
  photo_stage: optionalEnum(photoStages).describe("Photo Stage"),
  subject_tags: z.array(z.string()).default([]).describe("Subject Tags"),
});
export type MediaItemUpload = z.infer<typeof MediaItemUploadSchema>;

export const CreateMediaSessionSchema = z.object({
  submission_type: z
    .enum(submissionTypes)
    .default("field_note")
    .describe("Submission Type"),
  source: z.enum(mediaSources).default("api").describe("Source"),
  message: z.string().optional().describe("Message"),
  location_id: optionalUuid().describe("Location"),
  external_source: z.string().optional().describe("External Source"),
  external_id: z.string().optional().describe("External ID"),
  items: z.array(MediaItemUploadSchema).min(1).describe("Items"),
});
export type CreateMediaSession = z.infer<typeof CreateMediaSessionSchema>;

export const CompleteMediaItemSchema = z.object({
  public_url: z.string().describe("Public URL"),
  width_px: z.number().int().positive().optional().describe("Width (px)"),
  height_px: z.number().int().positive().optional().describe("Height (px)"),
  captured_at: z.string().optional().describe("Captured At"),
  gps_latitude: z.number().min(-90).max(90).optional().describe("GPS Latitude"),
  gps_longitude: z
    .number()
    .min(-180)
    .max(180)
    .optional()
    .describe("GPS Longitude"),
  exif_data: z.record(z.unknown()).optional().describe("EXIF Data"),
});
export type CompleteMediaItem = z.infer<typeof CompleteMediaItemSchema>;

export const AssociateEntitySchema = z.object({
  entity_type: z.enum(associableEntityTypes).describe("Entity Type"),
  entity_id: z.string().uuid().describe("Entity"),
});
export type AssociateEntity = z.infer<typeof AssociateEntitySchema>;

// ---------------------------------------------------------------------------
// Insert schemas (server fills business_id, account_id, id, timestamps, etc.)
// Used as the base for *ImportRow schemas in import.schema.ts (ADV-914).
// ---------------------------------------------------------------------------

export const MediaSubmissionInsertSchema = MediaSubmissionSchema.omit({
  id: true,
  business_id: true,
  account_id: true,
  submitted_at: true,
  submitted_by: true,
  deleted_at: true,
  items: true,
  subjects: true,
  // ADV-1344: feed-view-only resolved identity, never an insert input.
  actor_name: true,
  actor_avatar_url: true,
});
export type MediaSubmissionInsert = z.infer<typeof MediaSubmissionInsertSchema>;

// MediaItem insert schema. Omits server-generated fields and gps_latitude /
// gps_longitude (virtual API fields with no DB column — gps_location is the
// only persisted PostGIS column and is written via the
// ops.set_media_item_location RPC, not .insert()).
export const MediaItemInsertSchema = MediaItemSchema.omit({
  id: true,
  business_id: true,
  account_id: true,
  created_at: true,
  deleted_at: true,
  ai_caption: true,
  ai_caption_status: true,
  uploaded_at: true,
  gps_latitude: true,
  gps_longitude: true,
  // ADV-1162: set by the import pipeline's comm-attachment insert, never by an
  // external caller (operator/app uploads leave it null).
  import_job_id: true,
  // ADV-1123 / ADR-0045: async-pipeline-managed fields. The download/validate/
  // finalize pipeline writes these via UPDATE (or they fall back to DB
  // defaults); a caller never sets them on the insert path.
  processing_status: true,
  etag: true,
  last_modified: true,
  cache_control: true,
  content_sha256: true,
  content_dedup_of: true,
  staging_key: true,
  format: true,
  aspect_ratio: true,
  pixel_count: true,
  has_alpha: true,
  exif_orientation: true,
  color_space: true,
  density_dpi: true,
  camera_make: true,
  camera_model: true,
  retry_count: true,
  last_attempted_at: true,
  last_error_message: true,
  last_error_at: true,
  last_error_stage: true,
  last_error_category: true,
});
export type MediaItemInsert = z.infer<typeof MediaItemInsertSchema>;

export const MediaSubjectInsertSchema = MediaSubjectSchema.omit({
  id: true,
  business_id: true,
});
export type MediaSubjectInsert = z.infer<typeof MediaSubjectInsertSchema>;

export {
  submissionTypes,
  mediaSources,
  photoStages,
  aiCaptionStatuses,
  uploadStatuses,
  entityTypes,
  associableEntityTypes,
  processingStatuses,
  lastErrorStages,
  lastErrorCategories,
};
